import { useCallback, useState } from 'react';


import { useApolloClient } from '@apollo/client/react';

import { MarketplaceSaleChannelType, MdStoreFilterInput, SaleChannel } from '@anyx/common-graphql';
import { DEFAULT_PAGE_SIZE, ObjectUtils } from '@anyx/shared-utils';

import { STORES_CHANNELS } from './graphql';

export const useStoresChannels = () => {
  const apolloClient = useApolloClient();
  const [loading, setLoading] = useState(false);

  const fetchStoresChannels = useCallback(
    async ({ accountIds, storeIds }: MdStoreFilterInput) => {
      setLoading(true);
      try {
        const { data } = await apolloClient.query({
          query: STORES_CHANNELS,
          variables: {
            filter: {
              storeIds,
              accountIds,
            },
            pageNumber: 1,
            pageSize: DEFAULT_PAGE_SIZE,
          },
        });

        return (data?.MdStores.items?.flatMap((item) => item.channels) || []) as SaleChannel[];
      } finally {
        setLoading(false);
      }
    },
    [apolloClient]
  );

  const getStoresChannels = useCallback(
    async ({
      accountIds = [],
      storeIds = [],
      picks,
    }: MdStoreFilterInput & { picks: (keyof SaleChannel)[] }) => {
      const storesChannels = await fetchStoresChannels({ accountIds, storeIds });

      return storesChannels.map((c) => ObjectUtils.pick(c, picks));
    },
    [fetchStoresChannels]
  );

  const getStoresChannelsTypes = useCallback(
    async ({ accountIds = [], storeIds = [] }: MdStoreFilterInput) => {
      if (accountIds?.length === 0 && storeIds?.length === 0) {
        return ObjectUtils.arrayFromEnum<MarketplaceSaleChannelType>(MarketplaceSaleChannelType);
      }

      const storesChannels = await fetchStoresChannels({ accountIds, storeIds });

      return [
        ...new Set(
          (storesChannels.map((c) => ObjectUtils.pick(c, ['type'])) as SaleChannel[]).map(
            ({ type }) => type
          )
        ),
      ] as MarketplaceSaleChannelType[];
    },
    [fetchStoresChannels]
  );

  return { getStoresChannels, getStoresChannelsTypes, loading };
};
